import { QueryCache, QueryClient } from '@tanstack/react-query';
import { isSlowFailure } from '@utils/attemptDuration';
import {
  isFailedFetchError,
  isGraphQLSchemaMismatchError,
  reloadOnGraphQLSchemaMismatch,
} from '@utils/errors';

const MAX_NETWORK_RETRIES = 2;
/** TanStack Query's own default for queries. */
const DEFAULT_QUERY_RETRIES = 3;
const MAX_RETRY_DELAY_MS = 4000;

/**
 * Mutations default to `retry: 0`, so a single dropped packet on a mobile
 * connection loses the request outright and the user has to redo the whole
 * flow. Retry only when the connection failed fast with no response at all — a
 * real backend error (401, 500, business rule) must still surface immediately,
 * and a request that hung until it was cut off is not repeated: it would run
 * the same heavy job again and fail the same way.
 */
export const shouldRetryMutation = (
  failureCount: number,
  error: unknown
): boolean =>
  failureCount < MAX_NETWORK_RETRIES &&
  isFailedFetchError(error) &&
  !isSlowFailure(error);

/**
 * A query the schema rejected fails the same way on every attempt — skip the
 * retries and go straight to the reload in `createQueryCache`.
 */
export const createShouldRetryQuery =
  (maxRetries: number) =>
  (failureCount: number, error: unknown): boolean =>
    failureCount < maxRetries && !isGraphQLSchemaMismatchError(error);

export const getRetryDelay = (attemptIndex: number): number =>
  Math.min(1000 * 2 ** attemptIndex, MAX_RETRY_DELAY_MS);

/**
 * Every query of a client goes through its cache — including `fetchQuery` /
 * `ensureQueryData` outside `useCustomQuery`. A tab opened before a deploy that
 * changed the GraphQL schema reloads once to get the matching bundle.
 * Mutations are left out on purpose: a reload there would drop the text the
 * user is working on.
 */
export const createQueryCache = (): QueryCache =>
  new QueryCache({
    onError: (error) => {
      reloadOnGraphQLSchemaMismatch(error);
    },
  });

export const queryClient = new QueryClient({
  queryCache: createQueryCache(),
  defaultOptions: {
    queries: {
      retry: createShouldRetryQuery(DEFAULT_QUERY_RETRIES),
    },
    mutations: {
      retry: shouldRetryMutation,
      retryDelay: getRetryDelay,
    },
  },
});
